import { Grid, IconButton, Typography, makeStyles } from '@material-ui/core'
import Fade from '@material-ui/core/Fade'
import { Comment, Create } from '@material-ui/icons'
import AddCommentOutlined from '@material-ui/icons/AddCommentOutlined'
import DeleteIcon from '@material-ui/icons/Delete'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import HighlightOffOutlined from '@material-ui/icons/HighlightOffOutlined'
import {
  DataGrid,
  GridCellEditCommitParams,
  GridColumns,
  GridEditInputCell,
  GridRenderCellParams,
} from '@mui/x-data-grid'
import { createRef, useEffect, useRef, useState } from 'react'
import {
  Auto,
  CodeSet,
  CoderNote,
  CodesetResult,
  DateInline,
  FormApi,
  NdcInformation,
  Procedure,
  Provider,
  Search,
  autoCompleteRequest,
  cptEquals,
  diagnosisCodesByLinkString,
  filterForCodeset,
  findDxOrProcedureRequest,
  formApi,
  isReviewWorkflow,
  procedure,
  providerField,
  sanitizeDiagnosisLinksByLineNumber,
  setProcedureModifiers,
  truCode,
  utcDateString,
} from '../../core'
import { onEditAction } from '../../core/audit'
import {
  CollectionTable,
  Column,
  FilterControl,
  auditColumn,
  deleteColumn,
  editColumn,
  noteColumn,
  rowColumn,
  shimColumn,
} from '../../core/collections'
import { formatLocalDate } from '../../core/date-time'
import { Expansion, ExpansionDetails, ExpansionSummary } from '../../core/expansion'
import { personNameWithDegree, truncateSpan, withTooltip } from '../../core/format'
import { IcdCptApi } from '../../core/icd-cpt-api'
import { addStickyNote, removeStickyNote } from '../../core/notes-api'
import { mediumGray, orange, originalOrange } from '../../core/theme'
import { UndoRedo } from '../../core/undo-redo'
import { NO_NOTE_CONTEXT } from '../common/notes'

let lastCodeSet: CodeSet = null
let lastCode = ''
let lastCodeSetForTruCode: CodeSet = null
let lastMousedownTarget: Element | null = null
document.addEventListener('mousedown', (e: MouseEvent) => {
  lastMousedownTarget = e.target as Element
}, true)

const newCpt = (code: CodeSet, editingCpt: Procedure) => {
  if (!code) {
    return null
  }

  return {
    ...procedure({
      ...editingCpt,
      ...{
        ...code,
      },
      ...{
        description: code.description || code.longDescription,
        serviceDate: editingCpt.serviceDate,
        cptCode: code.code,
        isEMCode: code.em,
        promptNDC: code?.flags?.promptNDC || code?.promptNDC,
        globalDays: code.globalDays,
      },
    }),
  }
}

let inputEl: HTMLInputElement

function getSearchDate() {
  const date: Date = new Date()
  return utcDateString(date)
}

function getCodeSets(
  code: string,
  fullText = false,
  cptServiceDate?: Date | string,
): Promise<CodeSet[]> {
  const serviceDate = cptServiceDate ? utcDateString(cptServiceDate) : getSearchDate()
  return autoCompleteRequest({ code, serviceDate, fullText })
}


function swapCpt(api: IcdCptApi, cpt: Procedure, replacement: Procedure, id: string): Promise<boolean> {
  const cpts = api.cpts
  const values = cpts.actions.values()
  const exists = values.find((a) => cptEquals(a, replacement))
  if (!exists) {
    if (api.isReview) {
      api.reviewEdit(
        replacement,
        cpt,
        () => {
          cpts.actions.replaceAt(replacement, parseInt(id))
          return Promise.resolve(true)
        },
        () => Promise.resolve(false),
      )
    }
    cpts.actions.replaceAt(replacement, parseInt(id))
    return Promise.resolve(true)
  }
  return Promise.resolve(false)
}

async function replaceCpt(api: IcdCptApi, set: CodeSet, cpt: Procedure, id: string): Promise<boolean> {
  if (set?.codeType === 'ICD_10') {
    return Promise.resolve(false)
  }
  const { ndc } = api;
  function addNdcInfo(ndcCpt: Procedure) {
    return new Promise((resolve) => {
      if (ndcCpt.promptNDC) {
        ndc.actions.getState().edit(ndcCpt, (a: Procedure) => {
          resolve(a)
        })
      } else {
        resolve(ndcCpt)
      }
    })
  }

  const cpts = api.cpts
  const replacement = newCpt(set, cpt)
  const values = cpts.actions.values()
  const emCpt = values.find((a) => a.isEMCode)

  if (emCpt) {
    const i = values.indexOf(emCpt) + ''
    if (id !== i && (set.em || set.flags?.em)) {
      return Promise.resolve(false)
    }
  }

  await addNdcInfo(replacement);

  const exists = values.find((a) => cptEquals(a, replacement))
  if (!exists) {
    if (api.isReview) {
      api.reviewEdit(
        replacement,
        cpt,
        () => {
          cpts.actions.replaceAt(replacement, parseInt(id))
          api.onEditEnd(replacement)
          api.resetTopForm()
          return Promise.resolve(true)
        },
        () => Promise.resolve(false),
      )
    }
    cpts.actions.replaceAt(replacement, parseInt(id))
    return Promise.resolve(true)
  }
  return Promise.resolve(false)
}

export function cptEditCell(icdCpt: IcdCptApi) {
  return ({ row }: GridRenderCellParams) => {
    const cpt = row as Procedure
    const existing = { code: cpt.cptCode, description: { longDescription: cpt.description } }
    const initialValues = {
      cptCode: null,
    }

    let pendingCode = false
    async function findCode(v: string, onSuccess: (codsets: any[]) => void) {
      if (v?.length >= 2 && !pendingCode) {
        lastCode = v
        pendingCode = true
        let r: CodeSet[] = await getCodeSets(v, icdCpt.getFullText(), cpt.serviceDate)
        if (r) {
          r = r.filter((v: CodeSet) => v.codeType !== 'ICD_10')
          if (r.length) {
            lastCodeSet = r[0]
            onSuccess(r)
            pendingCode = false
            return Promise.resolve(true)
          }
        }
      }
      pendingCode = false
      return Promise.resolve(false)
    }

    function inputElement(): HTMLInputElement | null {
      const el: HTMLInputElement = cptCode.inputRef.current as any
      if (el && el.children) {
        inputEl = el.children[0] as any
        if (inputEl?.children?.length) {
          inputEl = inputEl.children[0] as any
        }
        return inputEl
      }
      return null
    }
    let input = ''
    let pendingSearch = false

    function setAutoComplete(r: CodeSet[]) {
      if (r && r.length) {
        lastCodeSetForTruCode = r[0]
        cptCode.list.getState().setValue(r)
      }
    }
    const search = async (v: string) => {
      if (!pendingSearch && v && v.length >= 2) {
        pendingSearch = true

        const r: CodeSet[] = await getCodeSets(v, icdCpt.getFullText(), cpt.serviceDate)
        setAutoComplete(r)
        pendingSearch = false
      }
    }
    async function setCode(v: string | number, doSearch = true) {
      input = (v || '').toString()

      const child: HTMLInputElement = inputElement()
      if (child) {
        cptCode.value.getState().setValue({ code: input })
        child.value = input
        child.focus()
        child.setSelectionRange(input.length, input.length)
        if (input?.length && doSearch) {
          await search(input)
        } else {
          cptCode.list.getState().setValue([])
        }
      }
    }

    const form: FormApi = useRef(
      formApi({
        values: initialValues,
        resetValues: null,
        fields: {
          cptCode: {
            label: null,
            required: true,
            list: [],
            inputRef: createRef(),
            disableClearable: false,
            style: { marginTop: 12 },
            listLabel: (c: CodeSet) => (c && c.description && c.code + ' - ' + c.description.longDescription) || '',
            textLabel: (c: CodeSet) => (c && c.code) || '',
            onEnter: async (v) => {
              await findCode(v?.code || lastCode, (code) => form.controls.cptCode.list.getState().setValue(code))
              if (lastCodeSet) {
                setCodeset(lastCodeSet)
              }
            },
            onClose: () => form.submit(),
            onEnterValid: () => {
              form.submit()
            },
            onChange: (c: CodeSet) => {
              if (c?.code) {
                lastCode = c.code
                if (row?.cptCode === c.code) {

                  if (row.promptNDC) {
                    icdCpt.ndc.actions.getState().edit({ ...row }, (a: Procedure) => {
                      swapCpt(icdCpt, row, a, row.id);
                    })

                  }
                }
              }
            },

            onInputChange: async (v: string) => {
              if (!v.length) {
                const c = form.controls.code;
                if (c) {
                  c.list.getState().setValue([])
                }
                return
              }
              if (v.length >= 2) {
                if (v.indexOf(' ') > -1) {
                  const filter = filterForCodeset(lastCodeSetForTruCode, 'CPT4')
                  // console.log('codeBook, filter', filter, lastCodeSet);
                  truCode.getState().openCodeBooks({
                    serviceDate: getSearchDate(),
                    filter,
                    query: v.replace(' ', '').trim(),
                    events: {
                      onCodePosted: (e) => {
                        if (e.codes && e.codes.length) {
                          findCode(e.codes[0].code, (sets) => {
                            replaceCpt(icdCpt, sets[0], cpt, row.id).then((wasReplaced) => {
                              if (wasReplaced) {
                                setCode(sets[0].code)
                              }
                            })
                          })
                        }
                      },
                    },
                  })
                  setCode(null)
                } else {
                  search(v)
                }
              }
              if (v?.length > 1) {
                lastCode = v
                findCode(v, (code) => {
                  form.controls.cptCode.list.getState().setValue(code);
                })
              } else {
                lastCode = lastCodeSet = null
              }

            },
          },
        },
        valid: (v: any) => {
          const { cptCode } = v
          const c: any = (cptCode?.code || lastCode || lastCodeSet?.code || '').trim()
          if (!c?.length) {
            return false
          }
          lastCode = c
          return true
        },
        onSubmit: async (values) => {
          const c: any = typeof values.cptCode === 'string' ? values.cptCode : lastCode || lastCodeSet?.code || null
          const v = typeof c === 'string' ? c : c ? c.code : ''
          // console.log('onSubmit code', v, values)
          if (!v?.length) {
            return
          }
          if (v === cpt.cptCode) {
            return
          }
          let serviceDate = getSearchDate()

          const codeResult: CodesetResult = await findDxOrProcedureRequest({
            serviceDate,
            code: v,
          })

          const { success } = codeResult

          if (success) {
            const set = codeResult.result
            setCodeset(set)
          } else {
            return
          }
        },
      }),
    ).current

    function setCodeset(set: CodeSet) {
      replaceCpt(icdCpt, set, cpt, row.id)
    }

    const { cptCode } = form.controls
    useEffect(() => {
      if (cptCode?.list) {
        cptCode.list.getState().setValue([existing])
        const r = inputElement()
        if (r) {
          r.focus()
        }
      }
    }, [])

    return <Search {...cptCode} />
  }
}

let edit: GridCellEditCommitParams = null

const providerEditInputCell = (providers: Provider[], label = '') => {
  return ({ api, row, field, id }: GridRenderCellParams) => {
    const cpt = row as Procedure

    const form = useRef(
      formApi({
        values: {
          control: null,
        },
        fields: {
          control: providerField(providers, {
            label,
            required: true,
            autoFocus: true,
            style: { width: '100%', marginTop: 12 },
            onChange: (e) => onChange(e),
            onEnter: () => {
              form.submit()
            },
          }),
        },
        valid: () => true,
        onSubmit: (v) => onChange(v.control),
      }),
    ).current

    const { control } = form.controls

    const onChange = (value: Provider) => {
      if (value?.providerId) {
        switch (field) {
          case 'provider':
            cpt.provider = value
            break
          case 'secondaryProvider':
            cpt.secondaryProvider = value
            break
          case 'tertiaryProvider':
            cpt.tertiaryProvider = value
            break
        }
        api.updateRows([{ ...cpt, ...{ id: id } }])
      }
    }
    return <Auto {...control} />
  }
}

const serviceDateEditInputCell = () => {
  return ({ api, row, id }: GridRenderCellParams) => {
    const cpt = row as Procedure

    const onChange = (value: Date) => {
      if (value) {
        cpt.serviceDate = value
        api.updateRows([{ ...cpt, ...{ id: id } }])
      }
    }

    const form = useRef(
      formApi({
        values: {
          control: cpt.serviceDate,
        },
        fields: {
          control: {
            onChange,
            label: '',
            required: true,
            autoFocus: true,
            style: { width: '100%', marginTop: 12 },
            inputProps: { style: { fontSize: 12, marginLeft: 12 } },
            minDate: undefined,
            maxDate: new Date(),
            onEnter: () => {
              form.submit()
            },
          },
        },
        valid: () => true,
        onSubmit: (v) => onChange(v.control),
      }),
    ).current

    const { control } = form.controls
    return <DateInline {...control} />
  }
}

const headerStyles = makeStyles({
  hideRightSeparator: {
    'minWidth': 50,
    '& > .MuiDataGrid-columnSeparator': {
      display: 'none',
    },
  },
})

const deleteHeaderStyles = makeStyles({
  hideRightSeparator: {
    'width': 70,
    'minWidth': 70,
    'maxWidth': 70,
    '& > .MuiDataGrid-columnSeparator': {
      display: 'none',
    },
  },
})

const gridStyles = makeStyles({
  root: {
    'border': 'none',
    '& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus': {
      outline: 'none',
    },
  },
})
let selected: number[] = []

function ndcTooltip(info: NdcInformation): string {
  return [
    info.ndcDescription && `Description: ${info.ndcDescription}`,
    info.ndcNumber && `NDC Number: ${info.ndcNumber}`,
    info.ndcMeasure && `Measure: ${info.ndcMeasure}`,
    info.ndcQuantity && `Quantity: ${info.ndcQuantity}`,
  ]
    .filter(Boolean)
    .join('\n')
}

function checkValue(e: any) {
  const t = e.target
  const min = parseInt(t.min)
  const max = parseInt(t.max)
  const value = parseInt(t.value)
  if (value > max) {
    t.value = min
  } else if (value < min) {
    t.value = max
  }
}
function useColumns(api: IcdCptApi & { rows: Procedure[]; inactive?: boolean }) {
  const { cpts, context, notesApi, rows, inactive } = api
  const { secondaryEnabled, tertiaryEnabled, config } = context

  const { primaryProviders, secondaryProviders, tertiaryProviders } = config
  const headerClasses = headerStyles()
  const columns: GridColumns = [
    {
      headerName: ' ',
      headerClassName: headerClasses.hideRightSeparator,
      field: 'lineNumber',
      width: 60,
      type: 'number',
      editable: !inactive,
      sortable: false,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) =>
        params.row.active ? (
          <div>{params.row.lineNumber}</div>
        ) : (
          <HighlightOffOutlined htmlColor={mediumGray} fontSize="small" />
        ),
      renderEditCell: (params) => (
        <GridEditInputCell
          {...params}
          inputProps={{
            min: 1,
            max: rows.length,
          }}
        />
      ),
    },
    {
      headerName: 'Code',
      headerClassName: headerClasses.hideRightSeparator,
      field: 'cptCode',
      width: 90,
      editable: true,
      sortable: false,
      headerAlign: 'left',
      align: 'left',
      renderCell: (params) =>
        params.row.ndcInformation?.ndcDescription
          ? withTooltip(ndcTooltip(params.row.ndcInformation), <span>{params.row.cptCode}</span>, 'bottom')
          : params.row.cptCode,
      renderEditCell: cptEditCell(api),
    },
    {
      headerName: 'Mod',
      headerClassName: headerClasses.hideRightSeparator,
      field: 'modifier',
      editable: true,
      sortable: false,
      headerAlign: 'center',
      align: 'center',
      width: 70,
    },
    {
      headerName: 'Mult',
      headerClassName: headerClasses.hideRightSeparator,
      field: 'multiplier',
      type: 'number',
      headerAlign: 'center',
      align: 'center',
      editable: true,
      sortable: false,
      width: 70,
      renderEditCell: (params) => (
        <GridEditInputCell
          {...params}
          inputProps={{
            min: 1,
            max: 999,
            onInput: checkValue,
          }}
        />
      ),
    },
    {
      headerName: 'Links',
      headerAlign: 'left',
      align: 'left',
      headerClassName: headerClasses.hideRightSeparator,
      field: 'links',
      editable: true,
      sortable: false,
    },
    {
      headerName: 'GD',
      headerClassName: headerClasses.hideRightSeparator,
      field: 'globalDays',
      headerAlign: 'center',
      align: 'center',
      editable: false,
      sortable: false,
      width: 60,
    },
    {
      headerName: 'Date',
      headerClassName: headerClasses.hideRightSeparator,
      field: 'serviceDate',
      width: 120,
      minWidth: 120,
      editable: true,
      sortable: false,
      type: 'date',
      // serviceDate is a calendar date, not an instant: format it timezone-safely so
      // it isn't shifted a day for users behind UTC (formatDate would .toLocal() the
      // UTC-midnight value and drop to the previous day west of UTC).
      renderCell: (params) => formatLocalDate(params.row.serviceDate, 'MM/dd/yyyy'),
      renderEditCell: serviceDateEditInputCell(),
    },
    {
      headerName: 'Description',
      headerClassName: headerClasses.hideRightSeparator,
      field: 'description',
      width: 120,
      minWidth: 120,
      editable: true,
      sortable: false,
      renderCell: (params) => {
        const ndcDesc = params.row.ndcInformation?.ndcDescription
        const displayText = ndcDesc || params.row.description
        
        if (params.row.ndcInformation) {
          // For NDC rows, show as clickable/editable
          return truncateSpan(displayText, '10vw')
        }
        
        // For non-NDC rows, display as read-only text
        return truncateSpan(displayText, '10vw')
      },
      renderEditCell: (params) => {
        if (params.row.ndcInformation) {
          const ndcDesc = params.row.ndcInformation.ndcDescription
          return <div style={{ padding: '4px 8px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ndcDesc}</div>
        }
        // For non-NDC rows, return null to prevent editing
        return null
      },
    },

    {
      headerName: 'Primary',
      headerClassName: headerClasses.hideRightSeparator,
      field: 'provider',
      editable: true,
      sortable: false,
      flex: 1,
      renderEditCell: providerEditInputCell(primaryProviders),
      valueGetter: (params) => personNameWithDegree(params.row.provider),
    },

    secondaryEnabled
      ? {
        headerName: 'Secondary',
        headerClassName: headerClasses.hideRightSeparator,
        field: 'secondaryProvider',
        editable: true,
        sortable: false,
        flex: 1,
        renderEditCell: providerEditInputCell(secondaryProviders),
        valueGetter: (params) => personNameWithDegree(params.row.secondaryProvider),
      }
      : null,
    tertiaryEnabled
      ? {
        headerName: 'Tertiary',
        headerClassName: headerClasses.hideRightSeparator,
        field: 'tertiaryProvider',
        editable: true,
        sortable: false,
        flex: 1,
        renderEditCell: providerEditInputCell(tertiaryProviders),
        valueGetter: (params) => personNameWithDegree(params.row.tertiaryProvider),
      }
      : null,
  ].filter((a) => a !== null) as GridColumns
  const openNotes = notesApi.useNotes((s) => s.openDialog)
  const onNoteClick = (e) => () =>
    openNotes(
      NO_NOTE_CONTEXT,
      e.row.note,
      async (note) => {
        await addStickyNote({ code: e.row.cptCode, note: note.note }, e.row)

        cpts.actions.replaceAt(e.row, e.id)
        cpts.actions.refresh()
      },
      async () => {
        await removeStickyNote(e.row)
        cpts.actions.replaceAt(e.row, e.id)
        cpts.actions.refresh()
      },
    )
  columns.push({
    headerName: ' ',
    field: 'note',
    headerClassName: deleteHeaderStyles().hideRightSeparator,
    editable: false,
    sortable: false,
    width: 12,
    align: 'left',
    renderCell: (e) => {
      const note: CoderNote = (e.row as any).note
      return note ? (
        withTooltip(
          note.note,
          <IconButton
            tabIndex={-1}
            disabled={inactive}
            color="primary"
            style={{ pointerEvents: 'all' }}
            onClick={onNoteClick(e)}
          >
            <Comment />{' '}
          </IconButton>,
          'bottom',
        )
      ) : (
        <IconButton tabIndex={-1} disabled={inactive} color="primary" onClick={onNoteClick(e)}>
          <AddCommentOutlined htmlColor={mediumGray} />
        </IconButton>
      )
    },
  })

  function onRemove(row: Procedure) {
    const found = api.cpts.actions.values().find((v) => cptEquals(v, row))
    if (found) {
      api.removeCpt(found)
      api.cpts.actions.refresh()
    }
  }

  columns.push({
    headerName: ' ',
    field: 'delete',
    align: 'right',
    headerClassName: deleteHeaderStyles().hideRightSeparator,
    editable: false,
    sortable: false,
    width: 12,
    renderCell: (e) => (
      <IconButton tabIndex={-1} disabled={inactive} onClick={() => onRemove(e.row)} color="primary">
        <DeleteIcon />
      </IconButton>
    ),
  })

  return columns
}

function CptEditGrid(api: IcdCptApi & { isReview: boolean; readonly: boolean }) {
  const { cpts, isReview, inactiveCpts, readonly } = api

  const allCpts = cpts.data((s) => s.values)
  const rows = allCpts.map((v, i) => ({ ...v, id: i + '', lineNumber: i + 1 }))
  const inactive = inactiveCpts.data((s) => s.values).map((v, i) => ({ ...v, id: i, lineNumber: rows.length + i + 1 }))

  const [indices, setIndices] = useState<number[]>(selected || [])
  const onSelectionModelChange = (i: number[]) => {
    setIndices(i)
    selected = i
  }

  function replace(cpt: Procedure, id: number) {
    const values = api.cpts.actions.values()
    values[id as number] = cpt
    cpts.actions.setActiveValuesDestructive(values)
  }
  let editPending = false
  let didCommit = false
  let originalProviderIds: { provider?: string; secondaryProvider?: string; tertiaryProvider?: string } = {}
  function reviewOrReplace(row: Procedure, id: number) {
    const values = api.cpts.actions.values()
    editPending = true
    // Capture now: didCommit reflects this edit at the moment reviewOrReplace runs,
    // and onEditEnd must only fire once the store actually holds the reviewed row
    // (after the audit dialog, if any, resolves) rather than before it's committed.
    const shouldEndEdit = didCommit
    const onDone = () => {
      if (shouldEndEdit) {
        api.onEditEnd(row)
      }
    }
    if (isReview) {
      api.reviewEdit(row, values[id], () => {
          replace(row, id)
          cpts.actions.refresh()
          onDone()
        },
        () => {
          replace(values[id], id)
          cpts.actions.refresh()
          editPending = false
          onDone()
        })
    } else {
      editPending = false
      replace(row, id)
      cpts.actions.refresh()
      onDone()
    }
  }


  const onEditStart = ({ row, id, field }) => {
    // For Description field, only allow editing for NDC codes
    if (field === 'description') {
      if (!row.ndcInformation) {
        return false
      }
      
      // For NDC codes, open NDC dialog when Description is clicked
      // Preserve original NDC values before dialog modifies them
      const originalNdcInfo = {
        ndcDescription: row.ndcInformation.ndcDescription,
        ndcNumber: row.ndcInformation.ndcNumber,
        ndcMeasure: row.ndcInformation.ndcMeasure,
        ndcQuantity: row.ndcInformation.ndcQuantity,
      }
      
      // Make a copy to pass to the dialog to avoid mutation
      const cptForDialog = { ...row }
      
      api.ndc.actions.getState().edit(cptForDialog, (updatedCpt: Procedure) => {
        // Always get the current row from the grid rows array (not the stale reference)
        const gridRow = rows[id]
        let hasChanges = false
        
        // Compare updated values against preserved original values
        if (updatedCpt.ndcInformation) {
          if (updatedCpt.ndcInformation.ndcDescription !== originalNdcInfo.ndcDescription ||
              updatedCpt.ndcInformation.ndcNumber !== originalNdcInfo.ndcNumber ||
              updatedCpt.ndcInformation.ndcMeasure !== originalNdcInfo.ndcMeasure ||
              updatedCpt.ndcInformation.ndcQuantity !== originalNdcInfo.ndcQuantity) {
            hasChanges = true
          }
        }
        
        if (hasChanges) {
          // Create updated row with new NDC info
          const updatedRow = {
            ...gridRow,
            ndcInformation: updatedCpt.ndcInformation
          }
          
          didCommit = true
          editPending = isReview
          reviewOrReplace(updatedRow, parseInt(id))
        }
      })
      return false
    }
    
    editPending = isReview;
    didCommit = false
    const cpt = row as Procedure
    originalProviderIds = {
      provider: cpt.provider?.providerId,
      secondaryProvider: cpt.secondaryProvider?.providerId,
      tertiaryProvider: cpt.tertiaryProvider?.providerId,
    }
    api.editCpt(row, false, () => {

      editPending = false
      setTimeout(() => {
        replace(row, id);
      }, 200);
    })
  }

  const onEditStop = ({ row, id, field }) => {
    if (field !== 'lineNumber' && field !== 'cptCode' && field !== 'description') {
      reviewOrReplace(row, id)

      const inSelection = indices.includes(id)
      const isProvider = field.toLowerCase().indexOf('provider') > -1

      if (inSelection) {
        indices.forEach((i) => {
          if (i !== id) {
            if (isProvider) {
              console.info('edit stop provider other ids', i, row[field])
              rows[i][field] = row[field]
            } else {
              onCellEditCommit({ ...edit, id: i })
              console.info('edit stop other ids', i, edit.value)
            }
            cpts.actions.replaceAt(rows[i], i)
          }
        })
      }
    }
    if (didCommit) {
      // onEditEnd now fires from reviewOrReplace's own completion (see above),
      // once the store actually reflects the reviewed edit. Use cancelEdit (not
      // resetTopForm) so the api's internal editingCpt reference set by
      // onEditStart is cleared too -- otherwise a later "add" from the top form
      // would still see it and replace this row instead of adding a new one.
      api.cancelEdit()
    }
    else {
      const clickTarget = lastMousedownTarget
      setTimeout(() => {
        const active = document.activeElement
        // Check 1: active element is inside top form
        if (active?.closest?.('[data-cpt-top-form]')) return
        // Check 2: mousedown target was inside top form
        if (clickTarget?.closest?.('[data-cpt-top-form]')) return
        // Check 3: active or click target is inside the top-form code Autocomplete portal popup
        const codeRef = (api as any).form?.controls?.code?.inputRef?.current as HTMLElement
        if (codeRef) {
          const codeInput = codeRef.querySelector?.('input')
          const popupId = codeInput?.getAttribute('aria-owns') || codeInput?.getAttribute('aria-controls')
          if (popupId) {
            const popup = document.getElementById(popupId)
            if (popup?.contains(active) || popup?.contains(clickTarget)) return
          }
        }
        // Same reasoning as above: clear editingCpt along with the form so a
        // cancelled inline edit doesn't get mistaken for an in-progress edit
        // the next time the top form is used to add a new CPT.
        api.cancelEdit()
      }, 50)
    }

    didCommit = false
    cpts.actions.refresh()
  }

  const onCellEditCommit = (params: GridCellEditCommitParams) => {
    const { id, field, value } = params
    const cpt = rows[id]
    switch (field) {
      case 'cptCode':
        if (!editPending && lastCode?.length && lastCode !== value) {
          didCommit = true
          console.info('lastCode was not used', lastCodeSet)
          if (lastCodeSet && lastCodeSet.code === lastCode) {
            console.info('lastCodeSet was used', lastCodeSet)
            replaceCpt(api, lastCodeSet, cpt, id + '')
          } else if (!editPending) {
            getCodeSets(lastCode as string, api.getFullText(), cpt.serviceDate).then((r) => {
              if (r && r.length) {
                lastCodeSet = r[0]
                replaceCpt(api, lastCodeSet, cpt, id + '')
              }
            })
         
          }
        }
        editPending = false
        break
      case 'lineNumber':
        const currentIndex = rows.indexOf(cpt)
        const newIndex = parseInt(value as string) - 1
        api.moveCpt({ currentIndex, newIndex })
        console.info('line number', currentIndex, newIndex)
        console.info('values after move', ...api.cpts.actions.values().map((v) => v.cptCode))
        api.cpts.actions.refresh()
        api.cpts.actions.setActiveValuesDestructive(api.cpts.actions.values())
        console.info('all values after move, refresh, post', ...api.cpts.actions.all().map((v) => v.cptCode))

        break
      case 'links':
        const dx = { diagnoses: api.icds.actions.values() }
        const originalLinks = cpt.links
        cpt.links = sanitizeDiagnosisLinksByLineNumber(value as string, dx)
        cpt.diagnosisList = diagnosisCodesByLinkString(dx, cpt.links)
        if (cpt.links !== originalLinks) didCommit = true
        break
      case 'modifier':
        if (value !== cpt.modifier) didCommit = true
        cpt.modifier = value as string
        setProcedureModifiers(cpt)
        reviewOrReplace(cpt, id as number)
        break
      case 'multiplier':
        const newMultiplier = Math.max(1, (value as number) || 0)
        if (newMultiplier !== cpt.multiplier) didCommit = true
        cpt.multiplier = newMultiplier
        break
      case 'serviceDate':
        if (value instanceof Date) {
          if (!cpt.serviceDate || value.getTime() !== new Date(cpt.serviceDate).getTime()) {
            didCommit = true
          }
          cpt.serviceDate = value as Date
        }
        break
      case 'provider':
        if (cpt.provider?.providerId !== originalProviderIds.provider) didCommit = true
        break
      case 'secondaryProvider':
        if (cpt.secondaryProvider?.providerId !== originalProviderIds.secondaryProvider) didCommit = true
        break
      case 'tertiaryProvider':
        if (cpt.tertiaryProvider?.providerId !== originalProviderIds.tertiaryProvider) didCommit = true
        break
    }
    edit = params
  }
  const rowHeight = 65
  const gridClasses: any = gridStyles()
  const columns = useColumns({ ...api, rows })
  const inactiveColumns = useColumns({ ...api, rows, inactive: true })

  const showInactive = isReview && inactive.length > 0

  const edited = cpts.data((s) => s.edited)
  const getRowClassName = ({ row }) => {
    if (!row.active) {
      return 'inactive-row'
    }
    return undefined
  }
  return (
    <Fade in={true} timeout={500}>
      <Grid
        container
        classes={gridClasses.root}
        direction="column"
        style={{ width: '100%', flexWrap: 'nowrap', overflow: 'hidden', pointerEvents: readonly ? 'none' : undefined }}
      >
        <Grid item style={{ flexGrow: 1, backgroundColor: '#fff', height: rows.length === 0 ? 45 : undefined }}>
          <DataGrid
            autoHeight
            disableColumnMenu
            disableColumnFilter
            disableColumnSelector
            disableSelectionOnClick
            hideFooter
            density="compact"
            editMode="cell"
            checkboxSelection={!isReview}
            columns={columns}
            rows={rows}
            headerHeight={rowHeight}
            rowHeight={rowHeight}
            pageSize={rows.length}
            selectionModel={indices}
            onCellEditStart={onEditStart}
            onCellEditStop={onEditStop}
            onCellEditCommit={onCellEditCommit}
            onSelectionModelChange={onSelectionModelChange}
            components={{
              NoRowsOverlay: () => <div />,
              NoResultsOverlay: () => <div />,
            }}
            getRowClassName={getRowClassName}
          />
        </Grid>

        {showInactive ? (
          <Grid item style={{ flexGrow: 1, backgroundColor: '#fff' }}>
            <DataGrid
              autoHeight
              disableColumnMenu
              disableColumnFilter
              disableColumnSelector
              disableSelectionOnClick
              hideFooter
              density="compact"
              editMode="cell"
              checkboxSelection={!isReview}
              columns={inactiveColumns}
              rows={inactive}
              headerHeight={0}
              rowHeight={rowHeight}
              pageSize={inactive.length}
              components={{
                NoRowsOverlay: () => <div />,
                NoResultsOverlay: () => <div />,
              }}
              getRowClassName={getRowClassName}
            />
          </Grid>
        ) : undefined}
      </Grid>
    </Fade>
  )
}

const rowStyle: any = {
  display: 'flex',
  flexDirection: 'row',
  gap: 0,
  alignItems: 'center',
  flexWrap: 'nowrap',
}

export function CptEditCollection(api: IcdCptApi) {
  const edit = true;
  const { cpts, notesApi, moveCpt, context, cptChanges, inactiveCpts } = api
  const { auditCPT } = context.config
  const { secondaryEnabled, tertiaryEnabled, readonly } = context

  const values = cpts.data((s) => s.values)

  const openNotes = notesApi.useNotes((s) => s.openDialog)

  const isReview = isReviewWorkflow()
  const edited = cpts.data((s) => s.edited)
  const disableDelete = !edited || readonly

  const actions = isReview
    ? [
      auditColumn(
        onEditAction(
          'Edit CPT Audit',
          (item) => {
            cpts.actions.refresh()
            cptChanges.update(item, 'UPDATE')
          },
          true,
          auditCPT,
        ),
      ),
    ]
    : []

  actions.push(
    noteColumn((e) =>
      openNotes(
        NO_NOTE_CONTEXT,
        e.value.note,
        async (note) => {
          await addStickyNote({ code: e.value.cptCode, note: note.note }, e.value)
          cpts.actions.refresh()
        },
        () => {
          removeStickyNote(e.value)
          cpts.actions.refresh()
        },
      ),
    ),
    editColumn<Procedure>(
      (e) => api.editCpt(e.value),
      (v) => disableDelete,
      '',
      (e) => 'cpt-edit-btn-' + e.row,
    ),
    deleteColumn<Procedure>(
      (e) => {
        api.removeCpt(e.value)
      },
      (v) => disableDelete,
      '',
      (e) => 'cpt-delete-btn-' + e.row,
    ),
    shimColumn(),
  )
  const columns: Column<Procedure>[] = [
    rowColumn(),
    {
      label: 'Code',
      name: 'cptCode',
      style: { whiteSpace: 'nowrap' },
    },
    {
      label: 'Mod',
      name: 'modifier',
      style: { whiteSpace: 'nowrap', width: 30, maxWidth: 30 },
    },
    {
      label: 'Mult',
      name: 'multiplier',
      style: { whiteSpace: 'nowrap', width: 20, maxWidth: 20 },
    },
    {
      label: 'Links',
      name: 'links',
      style: { whiteSpace: 'nowrap', width: 45, maxWidth: 45 },
    },
    {
      label: 'GD',
      name: 'globalDays',
      style: { whiteSpace: 'nowrap', width: 20, maxWidth: 20 },
    },
    {
      label: 'Date',
      name: 'serviceDate',
      style: { whiteSpace: 'nowrap', width: 90, maxWidth: 90 },
      component: (e) => formatLocalDate(e.value.serviceDate, 'MM/dd/yyyy'),
    },
    {
      label: 'Description',
      name: 'description',
      component: (e) => truncateSpan(e.value.description, '10vw'),
    },
    {
      label: 'Primary',
      name: 'physician',
      component: (e) => truncateSpan(personNameWithDegree(e.value.provider), '5vw'),
    },
    secondaryEnabled
      ? {
        label: 'Secondary',
        name: 'secondaryProvider',
        component: (e) => truncateSpan(personNameWithDegree(e.value.secondaryProvider), '4vw'),
      }
      : null,
    tertiaryEnabled
      ? {
        label: 'Tertiary',
        name: 'tertiaryProvider',
        component: (e) => truncateSpan(personNameWithDegree(e.value.tertiaryProvider), '4vw'),
      }
      : null,
    ...actions,
  ].filter((a) => a !== null)

  const [expanded, setExpanded] = useState(values.length > 0)
  const [clicked, setClicked] = useState(false)

  useEffect(() => {
    if (!clicked && !expanded) {
      setExpanded(values.length > 0)
    }
  })

  const onClicked = () => {
    setExpanded(!expanded)
    setClicked(true)
  }
  const rowColor = (cpt: Procedure) => (cpt === edited ? '#ffeedd' : undefined)
  return (
    <Expansion expanded={expanded} id="cpt_expansion">
      <ExpansionSummary onClick={onClicked} expandIcon={<ExpandMoreIcon id="cpt_expend_more" />}>
        <Grid container direction="row" alignItems="center" style={{ width: '100%' }}>
          <Grid item>
            <div style={rowStyle}>
              <Typography variant="subtitle2">{`CPT® ${values.length ? '- ' + values.length : ''}`}</Typography>
              <Typography id="cpt_ama_copyright" variant="caption" color="textSecondary" style={{ marginLeft: 12 }}>
                {`CPT® copyright ${new Date().getFullYear()} American Medical Association. All rights reserved.`}
              </Typography>
            </div>
          </Grid>
          <Grid item style={{ flexGrow: 1 }} />
          {

            /*
                        <Grid item>
                          <div style={rowStyle} onClick={(e) => e.stopPropagation()}>
                            <IconButton color="primary" onClick={() => setEdit(!edit)}>
                              <Create htmlColor={edit ? originalOrange : mediumGray} />
                            </IconButton>
                          </div>
                        </Grid>
                        */
          }
          <Grid item>
            <div style={rowStyle} onClick={(e) => e.stopPropagation()}>
              <UndoRedo api={api.cptChanges} htmlColor={orange} label="cpt_edit" />
              <FilterControl api={cpts} />
              <Fade in={true}>
                <div></div>
              </Fade>
            </div>
          </Grid>
        </Grid>
      </ExpansionSummary>
      <ExpansionDetails>
        {
          edit ? (
            <>
              <Grid container direction="column" style={{ width: '100%', flexWrap: 'nowrap' }}>
                <CptEditGrid {...{ ...api, isReview, readonly }} />
              </Grid>
            </>
          ) : (
            <Grid container direction="column" style={{ width: '100%', flexWrap: 'nowrap' }}>
              <CollectionTable
                name="cpt"
                readonly={api.context.readonly}
                onMove={moveCpt}
                columns={columns}
                api={cpts}
                draggable={true}
                containerProps={{ elevation: 0 }}
                rowColor={rowColor}
                label={`CPT ${values.length ? '- ' + values.length : ''}`}
              />
              {isReview ? (
                <CollectionTable
                  name="cpt_inactive"
                  readonly={api.context.readonly}
                  headless={true}
                  onMove={moveCpt}
                  noEdit={true}
                  draggable={false}
                  columns={columns}
                  api={inactiveCpts}
                  containerProps={{ elevation: 0 }}
                  rowColor={rowColor}
                  label={`CPT ${values.length ? '- ' + values.length : ''}`}
                />
              ) : null}
            </Grid>
          )

        }
      </ExpansionDetails>
    </Expansion>
  )
}
